<script setup lang="ts">
import {ref, set} from "vue"
import ListBrowserToolbar from "~components/admin/ListBrowserToolbar.vue"
import ListBrowserPagination from "~components/admin/ListBrowserPagination.vue"
import ExpandableJsonDetail from "~components/admin/ExpandableJsonDetail.vue"
import {useListBrowser} from "@/composables/useListBrowser"
import {formatDateTime, globParam, setQueryParam} from "@/common/utils/admin"

interface ClassWalletUser {
  first_name?: string
  last_name?: string
  email?: string
}

interface ClassWalletOrder {
  id: string
  order_id: string
  purchase_order_id: string | null
  status: string
  amount: string | null
  classwallet_user: ClassWalletUser | null
  product_count: number
  created_at: string
  updated_at: string
  products?: Array<{product_name: string; price: number; quantity: number}>
  _showDetails?: boolean
}

const orderFilter = ref("")
const statusFilter = ref("")

const {
  dateFrom, dateTo, currentPage, perPage, sortBy, sortDesc,
  totalRows, rows, lastPage, doSearch, onSortChanged,
  setDateRange, dateRangeActive,
} = useListBrowser({
  serviceUrl: "/service/objects/classwallet_order",
  defaultSortColumn: "created_at",
  dateColumn: "created_at",
  buildFilterParams(params) {
    const orderVal = orderFilter.value.trim()
    if (orderVal) {
      setQueryParam(params, "order_id_glob", globParam(orderVal))
    }
    if (statusFilter.value) setQueryParam(params, "status_eq", statusFilter.value)
  },
  readUrlParams(urlParams) {
    if (urlParams.has("order_id_glob")) {
      orderFilter.value = urlParams.get("order_id_glob")!.replace(/\*$/, "")
    }
    if (urlParams.has("status_eq")) {
      statusFilter.value = urlParams.get("status_eq")!
    }
  },
})

const fields = [
  {key: "created_at", label: "Date", sortable: true, thStyle: {maxWidth: "10em"}},
  {key: "order_id", label: "Order ID", sortable: true},
  {key: "status", label: "Status", sortable: true, thStyle: {maxWidth: "7em"}},
  {key: "amount", label: "Amount", sortable: true, thStyle: {maxWidth: "6em"}},
  {key: "updated_at", label: "Updated", sortable: true, thStyle: {maxWidth: "10em"}},
]

const onRowClicked = (item: ClassWalletOrder) => set(item, "_showDetails", !item._showDetails)

const rowClass = (item: ClassWalletOrder | null) => item && item.status !== "complete" ? "table-warning" : ""

const statusVariant = (s: string) => s === "complete" ? "success" : "warning"

const labelForStatus = (s: string) => ({payment_pending: "Pending", complete: "Complete", failed: "Failed", canceled: "Canceled"} as Record<string,string>)[s] || s

defineExpose({
  orderFilter, statusFilter,
  dateFrom, dateTo, currentPage, perPage, sortBy, sortDesc, rows, fields,
  doSearch, onRowClicked, rowClass, statusVariant, labelForStatus,
  setDateRange, dateRangeActive, formatDateTime,
})
</script>

<template>
  <div>
    <!-- Filter bar -->
    <b-row class="mb-3 align-items-end">
      <b-col cols="12" sm="3" class="mb-2 mb-sm-0">
        <label class="mb-1 small text-muted">Order ID</label>
        <b-form-input
          v-model="orderFilter"
          placeholder="e.g. cw_..."
          size="sm"
          @keydown.enter="doSearch()"
        />
      </b-col>
      <b-col cols="6" sm="2" class="mb-2 mb-sm-0">
        <label class="mb-1 small text-muted">Status</label>
        <b-form-select v-model="statusFilter" size="sm" @change="doSearch()">
          <option value="">All</option>
          <option value="payment_pending">Pending</option>
          <option value="complete">Complete</option>
          <option value="failed">Failed</option>
          <option value="canceled">Canceled</option>
        </b-form-select>
      </b-col>
      <b-col cols="6" sm="2" class="mb-2 mb-sm-0">
        <label class="mb-1 small text-muted">From</label>
        <b-form-input v-model="dateFrom" type="date" size="sm" @keydown.enter="doSearch()" />
      </b-col>
      <b-col cols="6" sm="2" class="mb-2 mb-sm-0">
        <label class="mb-1 small text-muted">To</label>
        <b-form-input v-model="dateTo" type="date" size="sm" @keydown.enter="doSearch()" />
      </b-col>
    </b-row>

    <list-browser-toolbar
      :date-range-active="dateRangeActive"
      @search="doSearch()"
      @set-date-range="setDateRange($event)"
    />

    <!-- Top pagination -->
    <list-browser-pagination
      :current-page="currentPage"
      :total-rows="totalRows"
      :per-page="perPage"
      :last-page="lastPage"
      @page-change="currentPage = $event; doSearch()"
      @per-page-change="perPage = $event; doSearch()"
    />

    <!-- Results -->

    <!-- Results -->
    <b-table
      :items="rows"
      :fields="fields"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      :current-page="currentPage"
      :per-page="perPage"
      primary-key="id"
      striped
      hover
      responsive
      show-empty
      empty-text="Click Search to load results"
      :tbody-tr-class="rowClass"
      @row-clicked="onRowClicked"
      @sort-changed="onSortChanged"
    >
      <template #cell(created_at)="data">
        <span class="date-cell">{{ formatDateTime(data.item.created_at) }}</span>
      </template>
      <template #cell(order_id)="data">
        <code>{{ data.item.order_id }}</code>
      </template>
      <template #cell(status)="data">
        <b-badge :variant="statusVariant(data.item.status)">
          {{ labelForStatus(data.item.status) }}
        </b-badge>
      </template>
      <template #cell(amount)="data">
        {{ data.item.amount ? '$' + parseFloat(data.item.amount).toFixed(2) : '—' }}
      </template>
      <template #cell(updated_at)="data">
        <span class="date-cell">{{ formatDateTime(data.item.updated_at) }}</span>
      </template>
      <template #row-details="data">
        <div class="p-3">
          <div class="row mb-2">
            <div class="col-sm-6">
              <dl class="row mb-0">
                <dt class="col-sm-5">Internal ID</dt>
                <dd class="col-sm-7"><code>{{ data.item.id }}</code></dd>
                <dt class="col-sm-5">Amount</dt>
                <dd class="col-sm-7">{{ data.item.amount ? '$' + parseFloat(data.item.amount).toFixed(2) : '—' }}</dd>
                <dt class="col-sm-5">Product Count</dt>
                <dd class="col-sm-7">{{ data.item.product_count }}</dd>
                <dt class="col-sm-5">PO ID</dt>
                <dd class="col-sm-7">
                  <code v-if="data.item.purchase_order_id">{{ data.item.purchase_order_id }}</code>
                  <span v-else class="text-muted">—</span>
                </dd>
                <dt class="col-sm-5">CW User</dt>
                <dd class="col-sm-7">
                  <template v-if="data.item.classwallet_user">
                    {{ data.item.classwallet_user.first_name }} {{ data.item.classwallet_user.last_name }}
                  </template>
                  <span v-else class="text-muted">—</span>
                </dd>
              </dl>
            </div>
            <div class="col-sm-6">
              <dl class="row mb-0">
                <dt class="col-sm-5">Created</dt>
                <dd class="col-sm-7">{{ formatDateTime(data.item.created_at) }}</dd>
                <dt class="col-sm-5">Updated</dt>
                <dd class="col-sm-7">{{ formatDateTime(data.item.updated_at) }}</dd>
                <dt class="col-sm-5">Status</dt>
                <dd class="col-sm-7">
                  <b-badge :variant="statusVariant(data.item.status)">{{ labelForStatus(data.item.status) }}</b-badge>
                </dd>
              </dl>
            </div>
          </div>
          <hr>
          <expandable-json-detail title="Order Details" :source="data.item" max-height="45em" />
        </div>
      </template>
    </b-table>
  </div>
</template>
